/**
 * Space channel notification dispatch: resolves a slug for click navigation and
 * delegates to {@link fireSpaceNotification} (toast + sound + native).
 */

import { useCallback, type MutableRefObject } from 'react';
import type { PublicSpace } from '@adieuu/shared';
import { fireSpaceNotification, type SpaceNotificationDeps } from '../../utils/spaceNotifications';

export interface SpaceNotificationOptions {
  isMention?: boolean;
  isViewingChannel?: boolean;
  channelId?: string;
  spaceId?: string;
  spaceSlug?: string;
  onClick?: () => void;
}

export interface SpaceNotificationsParams {
  toast: SpaceNotificationDeps['toast'];
  soundPref: SpaceNotificationDeps['soundPref'];
  mentionSoundPref: SpaceNotificationDeps['mentionSoundPref'];
  notifications: SpaceNotificationDeps['notifications'];
  audio: SpaceNotificationDeps['audio'];
  claimAchievement: (action: string) => void;
  navigate: (path: string) => void;
  /** Slug of the active Space, used as the final fallback for click nav. */
  activeSpaceSlug: string | null | undefined;
  spacesRef: MutableRefObject<PublicSpace[]>;
}

export function useSpaceNotifications(params: SpaceNotificationsParams) {
  const {
    toast,
    soundPref,
    mentionSoundPref,
    notifications,
    audio,
    claimAchievement,
    navigate,
    activeSpaceSlug,
    spacesRef,
  } = params;

  const fireNotification = useCallback(
    (title: string, body: string, options?: SpaceNotificationOptions) => {
      const slug =
        options?.spaceSlug ??
        (options?.spaceId
          ? spacesRef.current.find((s) => s.id === options.spaceId)?.slug
          : undefined) ??
        activeSpaceSlug;
      const navTo =
        options?.onClick ??
        (() => {
          if (slug && options?.channelId) navigate(`/s/${slug}/c/${options.channelId}`);
        });
      fireSpaceNotification(
        title,
        body,
        {
          onClick: navTo,
          nativeTag: 'space-channel-event',
          isMention: options?.isMention,
          isViewingChannel: options?.isViewingChannel,
        },
        {
          toast,
          soundPref,
          mentionSoundPref,
          notifications,
          audio,
          onWilhelmScream: () => claimAchievement('wilhelm_scream'),
        },
      );
    },
    [
      toast,
      soundPref,
      mentionSoundPref,
      notifications,
      audio,
      claimAchievement,
      navigate,
      activeSpaceSlug,
      spacesRef,
    ],
  );

  return { fireNotification };
}
